import { AfterViewInit, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { User } from '../../../core/models/user.interface';
import { AuthService } from '../../../core/services/auth.service';
import { Router, ActivatedRoute } from '@angular/router';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { UsersService } from '../../../core/services/users.service';
import { LoaderService } from '../../../core/shared/services/loader.service';
import { Subscription } from 'rxjs';
import { USER_TYPES } from '../../../core/shared/constants';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss',
  animations: [
      trigger('expandCollapse', [
        state('collapsed', style({ height: 0, padding: 0, opacity: 0, margin: 0, overflow: 'hidden' })),
        state('expanded', style({ height: '*', opacity: 1 })),
        transition('collapsed <=> expanded', animate('400ms cubic-bezier(0.25, 0.8, 0.25, 1)')),
      ])
    ]
})
export class ProfileComponent implements OnInit, AfterViewInit, OnDestroy {
  private authService: AuthService = inject(AuthService);
  private usersService: UsersService = inject(UsersService);
  private loader: LoaderService = inject(LoaderService);
  private route = inject(ActivatedRoute);
  user: User = this.authService.getUser()!;
  subscription: any = null;

  // Suscripción a los cambios en los parámetros de consulta
  private queryParamSubscription!: Subscription;

  //Responsive
  sectionsState = ['expanded', 'expanded', 'expanded'];
  isResponsive: boolean = false;
  debounceResize: any;

  constructor(private router: Router) {}

  ngOnInit() {
    this.checkIfResponsive();
    window.addEventListener('resize', () => {
        clearTimeout(this.debounceResize);
        this.debounceResize = setTimeout(() => this.checkIfResponsive(), 200);
    });
    
    // Nos suscribimos a los cambios en los parámetros de consulta
    this.queryParamSubscription = this.route.queryParams.subscribe(params => {
      // Siempre cargar los datos del usuario más recientes
      this.loadUserData();
    });
  }

  ngOnDestroy() {
    // Limpiamos la suscripción al destruir el componente
    if (this.queryParamSubscription) {
      this.queryParamSubscription.unsubscribe();
    }
    
    // Limpiar el evento de resize
    window.removeEventListener('resize', () => {});
  }

  /**
   * Carga los datos del usuario desde el servidor
   */
  loadUserData() {
    this.loader.show();
    
    // Primero obtenemos el usuario actual del servicio de autenticación
    // para asegurarnos de tener el id más reciente
    const authUser = this.authService.getAuthUser();
    if (!authUser || !authUser.id) {
      this.loader.hide();
      return;
    }
    
    // Usamos el id para obtener los datos completos del usuario
    this.usersService.getUserWithSubscription(authUser.id).subscribe({
      next: (response: any) => {
        if (response && response.data) {
          this.user = response.data;
          this.subscription = response.data.subscription;
        }
        this.loader.hide();
      },
      error: (error) => {
        console.error('Error obteniendo datos del usuario:', error);
        this.loader.hide();
      }
    });
  }

  checkIfResponsive() {
    this.isResponsive = window.innerWidth <= 768;
    if(!this.isResponsive) {
      this.sectionsState = ['expanded', 'expanded', 'expanded'];
    } else {
      this.sectionsState = ['expanded', 'collapsed', 'collapsed'];
    }
  }

  ngAfterViewInit() {
  }

  toggleSection(index: number) {
    if (this.isResponsive) {
      this.sectionsState[index] = this.sectionsState[index] === 'collapsed' ? 'expanded' : 'collapsed';
    }
  }

  editProfile() {
    this.router.navigate(['/dashboard/edit-profile'], { state: { user: this.user } });
  }

  isCoach(): boolean {
    return this.user && this.user.type && this.user.type.id === USER_TYPES.Coach.id;
  }

  editMentorProfile(): void {
    if (this.isCoach()) {
      this.router.navigate(['/dashboard/profile-mentor']);
    }
  }

  getValue(path: string, defaultValue: string = '-'): string {
    const keys = path.split('.');
    let value: any = this.user;
  
    for (const key of keys) {
      if (value && key in value) {
        value = value[key];
      } else {
        return defaultValue;
      }
    }
  
    return value? value : defaultValue;
  }

  /**
   * Obtiene la URL de la foto de perfil del usuario
   * Prioriza la foto en base64 almacenada en personalSection si existe
   */
  getProfilePhotoUrl(): string {
    if (this.user?.personalSection?.photoBase64) {
      return this.user.personalSection.photoBase64;
    }
    return this.user?.photo || 'assets/img/user-photo-default.png';
  }

  onPhotoError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = 'assets/img/user-photo-default.png';
  }

  // Método para verificar si la sección profesional está habilitada
  getDisabledSectProfessional(): boolean {
    // Por defecto, asumimos que está habilitado si no hay información específica
    // Esto puede ser ajustado según la lógica de negocio específica
    return this.user?.professionalSection?.isWorker !== false;
  }
  
  getPlanName(planId: string): string {
    switch(planId) {
      case 'premium': return 'Premium';
      case 'pro': return 'Profesional';
      case 'free': return 'Gratuito';
      default: return 'Freemium';
    }
  }
}
